import {
  computed,
  Injectable,
  resource,
  ResourceRef,
  Signal,
} from '@angular/core';
import {
  DEFAULT_CORPORATE_COLOR_PALETTE,
  DEFAULT_CORPORATE_DESIGN,
  EMessageType,
  ICorporateDesign,
  ICorporatePaletteColor,
} from '@web/dtos';
import { IApiResponse } from '@web/shared-types';
import { firstValueFrom } from 'rxjs';
import { CorporateDesignGateway } from './corporate-design.gateway';

@Injectable({
  providedIn: 'root',
})
export class CorporateDesignStore {
  private readonly gateway: CorporateDesignGateway =
    new CorporateDesignGateway();

  readonly #corporateDesignResource: ResourceRef<
    IApiResponse<ICorporateDesign> | undefined
  > = resource({
    loader: (): Promise<IApiResponse<ICorporateDesign>> => {
      return firstValueFrom(this.gateway.get());
    },
  });

  readonly isLoading: Signal<boolean> = computed((): boolean =>
    this.#corporateDesignResource.isLoading()
  );
  readonly corporateDesign: Signal<ICorporateDesign> = computed(
    (): ICorporateDesign => {
      const design: ICorporateDesign | undefined =
        this.#corporateDesignResource.hasValue()
          ? this.#corporateDesignResource.value()?.data
          : undefined;
      return this.normalizeCorporateDesign(design);
    }
  );

  updateCorporateDesign(query: ICorporateDesign): void {
    this.#corporateDesignResource.update((): IApiResponse<ICorporateDesign> => {
      return {
        data: query,
      };
    });
  }

  save(query: ICorporateDesign): Promise<ICorporateDesign | null> {
    return firstValueFrom(
      this.gateway.save(this.enhanceWithColorAdmixtures(query))
    ).then(
      (result: IApiResponse<ICorporateDesign>): ICorporateDesign | null => {
        if (result.data && result.message?.type === EMessageType.SUCCESS) {
          const design: ICorporateDesign = this.normalizeCorporateDesign(
            result.data
          );
          this.updateCorporateDesign(design);
          return design;
        }
        return null;
      }
    );
  }

  upload(query: FormData): Promise<ICorporateDesign | null> {
    return firstValueFrom(this.gateway.upload(query)).then(
      (result: IApiResponse<ICorporateDesign>): ICorporateDesign | null => {
        if (result.data && result.message?.type === EMessageType.SUCCESS) {
          const design: ICorporateDesign = this.normalizeCorporateDesign(
            result.data
          );
          this.updateCorporateDesign(design);
          return design;
        }
        return null;
      }
    );
  }

  enhanceWithColorAdmixtures(query: ICorporateDesign): ICorporateDesign {
    if (query.colorAdmixtures) {
      for (let color of Object.entries(query.colorAdmixtures)) {
        if (color === undefined) {
          color = this.corporateDesign().colorAdmixtures[color];
        }
      }
    } else if (query.colorAdmixtures === undefined) {
      query.colorAdmixtures = this.corporateDesign().colorAdmixtures;
    }
    return query;
  }

  private normalizeCorporateDesign(
    design: ICorporateDesign | undefined
  ): ICorporateDesign {
    const corporateDesign: ICorporateDesign = {
      ...DEFAULT_CORPORATE_DESIGN,
      ...design,
    };
    const colorPalette: ICorporatePaletteColor[] = (
      design?.colorPalette ?? DEFAULT_CORPORATE_COLOR_PALETTE
    ).map(
      (color: ICorporatePaletteColor): ICorporatePaletteColor => ({ ...color })
    );
    const normalizedPrimaryColor: string = corporateDesign.primaryColor
      .replace(/^#/, '')
      .toUpperCase();
    const primaryColorName: string =
      design?.primaryColorName ??
      colorPalette.find(
        (color: ICorporatePaletteColor): boolean =>
          color.value.toUpperCase() === normalizedPrimaryColor
      )?.name ??
      `#${normalizedPrimaryColor}`;

    return {
      ...corporateDesign,
      colorPalette,
      primaryColorName,
    };
  }
}
